import type { SchoolClassDetail } from 'src/modules/School/composables/useSchoolData';
import { schoolUserDisplayName } from 'src/modules/School/utilities/schoolUserDisplayName';

type Enrollment = NonNullable<SchoolClassDetail['enrollments']>[number];
type ClassSection = NonNullable<SchoolClassDetail['assigned_sections']>[number];

/**
 * Students the assignment dialog offers: ACTIVE enrollments only. Pending,
 * dropped, declined and completed students must not receive new
 * courses/sections — including through "Select all".
 */
export function assignableStudentOptions(enrollments: Enrollment[] = []) {
  return enrollments
    .filter(
      (row) =>
        !!row.student?.documentId &&
        (row.status ?? row.enrollment_status) === 'active',
    )
    .map((row) => ({
      value: row.student?.documentId || '',
      label: schoolUserDisplayName(row.student),
    }));
}

/**
 * Sections the assignment dialog offers: EVERY section assigned to the class,
 * sorted by name. The tab's list (assigned-sections) holds only the 12 most
 * recent, so searching it alone could never find an older section. Sections
 * assigned since the class detail loaded are merged in from that list.
 */
export function assignableSectionOptions(
  classSections: ClassSection[] = [],
  recentSections: Array<{ documentId: string; section_name: string }> = [],
) {
  const byId = new Map<string, string>();
  [...classSections, ...recentSections].forEach((section) => {
    if (section?.documentId) {
      byId.set(section.documentId, section.section_name || '');
    }
  });

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
}

// Students who left or never joined the class. Completed students are not
// inactive, so a finished semester still shows its results by default.
const INACTIVE_ENROLLMENT_STATUSES = new Set([
  'dropped',
  'declined',
  'pending',
]);

/**
 * Whether a Courses/Sections tab row belongs to an inactive student: hidden
 * by default, never given new work, but still unassignable. Rows without a
 * status count as active.
 */
export function isInactiveEnrollment(status?: string) {
  return INACTIVE_ENROLLMENT_STATUSES.has((status || '').toLowerCase());
}
